/**
 * Generateur d'identifiants (« Username generator » de Bitwarden, « Username »
 * de Proton Pass) — module pur, sans etat ni reseau.
 *
 * Trois formes, celles des leaders hors fournisseur d'alias (deja couvert par
 * `email-alias.ts`, R19) :
 *  - `word`     : un mot tire de la liste embarquee, + 4 chiffres en option ;
 *  - `plus`     : l'adresse de l'utilisateur sous-adressee
 *                 (`moi+k3x9q2ab@exemple.eu`) : une adresse par site, sans
 *                 fournisseur tiers, et le suffixe designe le site qui a fuite ;
 *  - `catchall` : une adresse aleatoire sur un domaine « attrape-tout » que
 *                 l'utilisateur possede (`k3x9q2ab@exemple.eu`).
 *
 * Pour `plus` et `catchall`, le suffixe est aleatoire ou, au choix, le nom du
 * site de la fiche (`moi+github.com@exemple.eu`) : option « Website » de
 * Bitwarden. Plus lisible pour trier le courrier, mais devinable d'un site a
 * l'autre -- l'aleatoire reste le defaut.
 *
 * Tout tirage passe par le CSPRNG du coeur (`randomInt` / `randomPick`, sans
 * biais modulo) : un suffixe previsible relierait les comptes entre eux.
 * L'adresse et le domaine de l'utilisateur ne sont jamais persistes ici.
 */
import { randomInt, randomPick } from '@klavox/crypto-core';
import { openableUri } from './entry-uris.js';

export type UsernameKind = 'word' | 'plus' | 'catchall';
/** Suffixe des formes `plus` et `catchall` : tire au hasard, ou nom du site de la fiche. */
export type UsernameSuffix = 'random' | 'site';

export interface UsernameSettings {
  readonly kind: UsernameKind;
  readonly capitalize: boolean;
  readonly includeNumber: boolean;
  readonly suffix: UsernameSuffix;
  /** Adresse a sous-adresser (forme `plus`). */
  readonly email: string;
  /** Domaine attrape-tout (forme `catchall`). */
  readonly domain: string;
}

export const DEFAULT_USERNAME_SETTINGS: UsernameSettings = {
  kind: 'word',
  capitalize: false,
  includeNumber: true,
  suffix: 'random',
  email: '',
  domain: '',
};

export const USERNAME_KINDS: readonly { readonly value: UsernameKind; readonly label: string }[] = [
  { value: 'word', label: 'Mot aleatoire' },
  { value: 'plus', label: 'Adresse e-mail + suffixe' },
  { value: 'catchall', label: 'Adresse sur mon domaine (attrape-tout)' },
];

export const USERNAME_SUFFIXES: readonly { readonly value: UsernameSuffix; readonly label: string }[] = [
  { value: 'random', label: 'Aleatoire' },
  { value: 'site', label: 'Nom du site de la fiche' },
];

/** 8 caracteres sur 36 = 41 bits : impossible a deviner d'un site a l'autre. */
export const RANDOM_SUFFIX_LENGTH = 8;
const SUFFIX_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const MAX_LOCAL_PART = 64;
const MAX_DOMAIN = 253;

export class UsernameGeneratorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UsernameGeneratorError';
  }
}

/** Valeur relue d'un formulaire : tout ce qui n'est pas une forme connue retombe sur `word`. */
export function usernameKind(value: unknown): UsernameKind {
  return value === 'plus' || value === 'catchall' ? value : 'word';
}

/** Valeur relue d'un formulaire : tout ce qui n'est pas `site` reste aleatoire. */
export function usernameSuffix(value: unknown): UsernameSuffix {
  return value === 'site' ? 'site' : 'random';
}

/**
 * Nom de site a poser en suffixe, tire de la premiere adresse web des adresses de la fiche (`Site (URL)` d'abord) :
 * l'hote en minuscules, sans `www.` (`https://www.GitHub.com/login` -> `github.com`), IDN en punycode. Les
 * caracteres d'un hote (lettres, chiffres, `-`, `.`) sont tous permis dans la partie locale d'une adresse. Aucune
 * adresse web : `null`.
 */
export function siteSuffixFromUris(uris: readonly string[]): string | null {
  for (const uri of uris) {
    const href = openableUri(uri);
    if (href === null) continue;
    const host = new URL(href).hostname.toLowerCase().replace(/^www\./u, '');
    if (/^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/u.test(host) && !host.includes('..')) return host;
  }
  return null;
}

export function randomSuffix(length = RANDOM_SUFFIX_LENGTH): string {
  let out = '';
  for (let i = 0; i < length; i++) out += SUFFIX_ALPHABET[randomInt(SUFFIX_ALPHABET.length)];
  return out;
}

/** Domaine DNS plausible (au moins un point, etiquettes LDH), en minuscules, ou `null`. */
export function normalizeDomain(raw: string): string | null {
  const domain = raw.trim().replace(/^@/u, '').replace(/\.$/u, '').toLowerCase();
  if (domain.length === 0 || domain.length > MAX_DOMAIN) return null;
  const labels = domain.split('.');
  if (labels.length < 2) return null;
  const ok = labels.every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/u.test(label));
  return ok && /[a-z]/u.test(labels[labels.length - 1]!) ? domain : null;
}

/**
 * `moi+k3x9q2ab@exemple.eu`. Une sous-adresse deja presente (`moi+news@`) est
 * remplacee, pas empilee : `moi+news+k3x9…` serait mal route par certains
 * fournisseurs.
 */
function plusAddressed(email: string, suffix: string): string {
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0 || trimmed.indexOf('@') !== at) throw new UsernameGeneratorError('Adresse e-mail invalide : saisissez par exemple moi@exemple.eu.');
  const local = trimmed.slice(0, at).split('+')[0]!;
  const domain = normalizeDomain(trimmed.slice(at + 1));
  if (local.length === 0 || /[\s"(),:;<>[\\\]]/u.test(local) || domain === null) {
    throw new UsernameGeneratorError('Adresse e-mail invalide : saisissez par exemple moi@exemple.eu.');
  }
  if (local.length + 1 + suffix.length > MAX_LOCAL_PART) {
    throw new UsernameGeneratorError("Adresse e-mail trop longue pour recevoir un suffixe.");
  }
  return `${local}+${suffix}@${domain}`;
}

function catchAll(rawDomain: string, local: string): string {
  const domain = normalizeDomain(rawDomain);
  if (domain === null) throw new UsernameGeneratorError('Domaine invalide : saisissez par exemple exemple.eu.');
  if (local.length > MAX_LOCAL_PART) throw new UsernameGeneratorError('Nom du site trop long pour une adresse e-mail : choisissez le suffixe aleatoire.');
  return `${local}@${domain}`;
}

/** Suffixe de l'adresse : aleatoire, ou le nom du site -- jamais un tirage silencieux si la fiche n'a pas de site. */
function addressSuffix(settings: UsernameSettings, site: string | null): string {
  if (settings.suffix === 'random') return randomSuffix();
  if (site === null) {
    throw new UsernameGeneratorError('Aucune adresse web dans la fiche : renseignez Site (URL) ou choisissez le suffixe aleatoire.');
  }
  return site;
}

function randomWord(wordlist: readonly string[] | undefined, settings: UsernameSettings): string {
  // Lettres seules : un identifiant avec tiret ou espace est refuse par trop de sites.
  const words = (wordlist ?? []).filter((word) => /^[a-z]{3,}$/u.test(word));
  if (words.length === 0) throw new UsernameGeneratorError('Liste de mots indisponible (hors ligne ?).');
  const word = randomPick(words);
  const base = settings.capitalize ? word[0]!.toUpperCase() + word.slice(1) : word;
  return settings.includeNumber ? base + String(randomInt(10_000)).padStart(4, '0') : base;
}

/**
 * Tire un identifiant selon la forme choisie ; leve `UsernameGeneratorError` si la saisie ne le permet pas.
 * `site` : suffixe « nom du site » (`siteSuffixFromUris`), `null` hors d'une fiche.
 */
export function generateUsername(settings: UsernameSettings, wordlist?: readonly string[], site: string | null = null): string {
  switch (settings.kind) {
    case 'plus':
      return plusAddressed(settings.email, addressSuffix(settings, site));
    case 'catchall':
      return catchAll(settings.domain, addressSuffix(settings, site));
    default:
      return randomWord(wordlist, settings);
  }
}
